import { useMemo } from 'react';

import { useChatsPageCustomerNumberUsageQuery } from '../../../generated';
import { useDebounce } from '../../../hooks';
import {
  CustomerNumberUsage,
  getOtherChatsUsingCustomerNumber,
} from '../utils/customerNumberUsage';

type UseCustomerNumberUsageParams = {
  supplierEntityId: string | null | undefined;
  customerNumber: string;
  chatId: string;
};

/**
 * Looks up which other chats of the same distributor already use the
 * customer number being typed. Lookup failures resolve to no matches: the
 * result only feeds a warning, so it must never block the form.
 */
export const useCustomerNumberUsage = ({
  supplierEntityId,
  customerNumber,
  chatId,
}: UseCustomerNumberUsageParams): {
  otherChats: CustomerNumberUsage[];
  isLoading: boolean;
} => {
  const term = useDebounce(customerNumber.trim(), 400);

  const { data, loading } = useChatsPageCustomerNumberUsageQuery({
    variables: { supplierId: supplierEntityId ?? '', customerNumber: term },
    skip: !supplierEntityId || !term,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const otherChats = useMemo(
    () =>
      term
        ? getOtherChatsUsingCustomerNumber(
            data?.searchCustomerChats,
            term,
            chatId
          )
        : [],
    [data, term, chatId]
  );

  return { otherChats, isLoading: loading };
};
